import React, { useEffect, useRef, useState } from 'react';
import { CreditCard, RefreshCw, Plus, ArrowLeft, ArrowRight, Ticket, CalendarDays, Clock, Minus } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const FormularioVenta = ({
  nuevaVenta,
  setNuevaVenta,
  medioPago,
  setMedioPago,
  mediosDePago,
  extrasDisponibles = [],
  valorEntrada,
  modoPantallaCompleta,
  setModoPantallaCompleta,
  modoPasoAPaso,
  setModoPasoAPaso,
  handleAgregarVenta,
  limpiarFormulario,
  resetStepTrigger
}) => {
  const cantidadRef = useRef(null);
  const diasRef = useRef(null);
  const monto1Ref = useRef(null);
  const monto2Ref = useRef(null);
  const continuarExtrasRef = useRef(null);
  const continuarPagoRef = useRef(null);
  const confirmarRef = useRef(null);

  const [pasoVenta, setPasoVenta] = useState(1);
  const [direccionPaso, setDireccionPaso] = useState('forward');
  const [pasoConError, setPasoConError] = useState(null);

  useEffect(() => {
    if (!modoPantallaCompleta || (modoPantallaCompleta && !modoPasoAPaso)) {
      cantidadRef.current?.focus();
      cantidadRef.current?.select();
      return;
    }

    if (pasoVenta === 1) {
      cantidadRef.current?.focus();
      cantidadRef.current?.select();
    } else if (pasoVenta === 2) {
      diasRef.current?.focus();
      diasRef.current?.select();
    } else if (pasoVenta === 3) {
      continuarExtrasRef.current?.focus();
    } else if (pasoVenta === 4) {
      continuarPagoRef.current?.focus();
    } else if (pasoVenta === 5) {
      confirmarRef.current?.focus();
    }
  }, [modoPantallaCompleta, modoPasoAPaso, pasoVenta]);

  const valorEntradaNumero = Number(valorEntrada || 0);
  const cantidadNumeroRaw = Number(nuevaVenta.cantidad);
  const cantidadNumero =
    Number.isFinite(cantidadNumeroRaw) && cantidadNumeroRaw > 0
      ? Math.floor(cantidadNumeroRaw)
      : 0;
  const diasNumeroRaw = Number(nuevaVenta.diasEstadia);
  const diasNumero =
    Number.isFinite(diasNumeroRaw) && diasNumeroRaw > 0
      ? Math.floor(diasNumeroRaw)
      : 1;
  const extrasSeleccionados = Array.isArray(nuevaVenta.extras) ? nuevaVenta.extras : [];
  const extrasActivos = (extrasDisponibles || []).filter((extra) => extra.activo !== false);
  const subtotalExtras = extrasSeleccionados.reduce(
    (sum, extra) => sum + Number(extra.valor || 0) * Number(extra.cantidad || 0),
    0
  );
  const totalBase = cantidadNumero > 0 && diasNumero > 0
    ? cantidadNumero * valorEntradaNumero * diasNumero
    : 0;
  const totalCalculado = totalBase + subtotalExtras;

  let fechaValidez = null;
  if (diasNumero > 0) {
    fechaValidez = new Date();
    fechaValidez.setDate(fechaValidez.getDate() + diasNumero);
    fechaValidez.setHours(10, 0, 0, 0);
  }

  useEffect(() => {
    if (nuevaVenta.medio_pago_id_2 != null) {
      monto1Ref.current?.focus();
    }
  }, [nuevaVenta.medio_pago_id_2]);

  useEffect(() => {
    if (!modoPantallaCompleta || !modoPasoAPaso) return;
    setPasoVenta(1);
    setDireccionPaso('forward');
  }, [modoPantallaCompleta, modoPasoAPaso]);

  useEffect(() => {
    if (resetStepTrigger == null) return;
    setPasoVenta(1);
    setDireccionPaso('forward');
    setPasoConError(null);
  }, [resetStepTrigger]);

  useEffect(() => {
    if (!modoPantallaCompleta || !modoPasoAPaso) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' || event.key === 'ArrowLeft') {
        if (pasoVenta > 1) {
          event.preventDefault();
          setDireccionPaso('back');
          setPasoVenta((prev) => Math.max(1, prev - 1));
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modoPantallaCompleta, modoPasoAPaso, pasoVenta]);

  const pasosVenta = [
    { numero: 1, titulo: 'Personas' },
    { numero: 2, titulo: 'Estadía' },
    { numero: 3, titulo: 'Extras' },
    { numero: 4, titulo: 'Pago' },
    { numero: 5, titulo: 'Confirmación' },
  ];

  const marcarErrorPaso = () => {
    setPasoConError(pasoVenta);
    window.setTimeout(() => setPasoConError(null), 350);
  };

  const avanzarPaso = () => {
    if (pasoVenta === 1 && cantidadNumero <= 0) {
      marcarErrorPaso();
      return;
    }

    if (pasoVenta === 2 && diasNumero <= 0) {
      marcarErrorPaso();
      return;
    }

    if (pasoVenta === 4 && !medioPago) {
      marcarErrorPaso();
      return;
    }

    if (pasoVenta === 5) {
      handleAgregarVenta();
      return;
    }

    setDireccionPaso('forward');
    setPasoVenta((prev) => Math.min(5, prev + 1));
  };

  const volverPaso = () => {
    if (pasoVenta === 1) return;
    setDireccionPaso('back');
    setPasoVenta((prev) => Math.max(1, prev - 1));
  };

  const manejarToggleExtra = (extra) => {
    setNuevaVenta((prev) => {
      const listaActual = Array.isArray(prev.extras) ? prev.extras : [];
      const existe = listaActual.find((item) => item.id === extra.id);

      if (existe) {
        return {
          ...prev,
          extras: listaActual.filter((item) => item.id !== extra.id),
        };
      }

      return {
        ...prev,
        extras: [
          ...listaActual,
          {
            id: extra.id,
            nombre: extra.nombre,
            valor: Number(extra.valor || 0),
            cantidad: 1,
          },
        ],
      };
    });
  };

  const ajustarCantidadExtra = (extra, delta) => {
    setNuevaVenta((prev) => {
      const listaActual = Array.isArray(prev.extras) ? prev.extras : [];
      const existente = listaActual.find((item) => item.id === extra.id);

      if (!existente) {
        if (delta <= 0) return prev;
        return {
          ...prev,
          extras: [
            ...listaActual,
            {
              id: extra.id,
              nombre: extra.nombre,
              valor: Number(extra.valor || 0),
              cantidad: 1,
            },
          ],
        };
      }

      const cantidadActual = Number(existente.cantidad || 0);
      const nuevaCantidad = cantidadActual + delta;

      if (nuevaCantidad <= 0) {
        return {
          ...prev,
          extras: listaActual.filter((item) => item.id !== extra.id),
        };
      }

      return {
        ...prev,
        extras: listaActual.map((item) =>
          item.id === extra.id ? { ...item, cantidad: nuevaCantidad } : item
        ),
      };
    });
  };

  const manejarCantidadExtra = (extraId, cantidad) => {
    const cantidadNormalizada = Math.max(1, Math.floor(Number(cantidad) || 1));
    setNuevaVenta((prev) => {
      const listaActual = Array.isArray(prev.extras) ? prev.extras : [];
      return {
        ...prev,
        extras: listaActual.map((item) =>
          item.id === extraId
            ? { ...item, cantidad: cantidadNormalizada }
            : item
        ),
      };
    });
  };

  const obtenerCantidadExtra = (extraId) => {
    const encontrado = extrasSeleccionados.find((item) => item.id === extraId);
    return encontrado ? encontrado.cantidad : 1;
  };

  const actualizarCampoNumerico = (campo, delta, minimo = 0) => {
    setNuevaVenta((prev) => {
      const valorActual = Math.floor(Number(prev[campo] || 0)) || minimo;
      const nuevoValor = Math.max(minimo, valorActual + delta);
      return {
        ...prev,
        [campo]: String(nuevoValor),
      };
    });
  };

  useEffect(() => {
    if (nuevaVenta.medio_pago_id_2 != null) {
      setNuevaVenta((prev) => {
        if (prev.medio_pago_id_2 == null) return prev;

        const monto1 = Number(prev.monto_medio_pago_1 || 0);
        const monto2 = Number(prev.monto_medio_pago_2 || 0);
        const suma = monto1 + monto2;

        if (Math.abs(suma - totalCalculado) <= 0.01) {
          return prev;
        }

        if (suma === 0) {
          return {
            ...prev,
            monto_medio_pago_1: totalCalculado,
            monto_medio_pago_2: 0,
          };
        }

        const proporcion = monto1 / suma;
        const nuevoMonto1 = Number((totalCalculado * proporcion).toFixed(2));
        const nuevoMonto2 = Number((totalCalculado - nuevoMonto1).toFixed(2));

        return {
          ...prev,
          monto_medio_pago_1: nuevoMonto1,
          monto_medio_pago_2: nuevoMonto2,
        };
      });
    } else {
      setNuevaVenta((prev) => {
        if (Number(prev.monto_medio_pago_1 || 0) === Number(totalCalculado)) {
          return prev;
        }

        return {
          ...prev,
          monto_medio_pago_1: totalCalculado,
          monto_medio_pago_2: '',
        };
      });
    }
  }, [totalCalculado, nuevaVenta.medio_pago_id_2, setNuevaVenta]);

  const containerClasses = modoPantallaCompleta
    ? "relative rounded-xl shadow-lg fixed inset-0 z-50 overflow-y-auto bg-gradient-to-br from-azul-oscuro via-azul-oscuro to-azul-lago px-4 sm:px-8 py-10"
    : "relative rounded-xl shadow-lg bg-azul-oscuro/40 backdrop-blur p-6";

  const panelClasses = modoPantallaCompleta
    ? "w-full max-w-7xl mx-auto bg-azul-oscuro bg-opacity-80 border border-blanco-puro/20 rounded-2xl px-6 sm:px-10 py-8 shadow-2xl backdrop-blur"
    : "";

  const gridClasses = modoPantallaCompleta
    ? "grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10"
    : "gap-4";

  return (
    <div className={containerClasses}>
      {modoPantallaCompleta && (
        <button
          onClick={() => setModoPantallaCompleta(false)}
          className="absolute top-8 right-10 text-amarillo-dorado hover:text-amber-300 bg-azul-oscuro bg-opacity-70 p-3 rounded-full shadow-lg transition"
        >
          <ArrowLeft size={32} />
        </button>
      )}

      <div className={panelClasses}>
        <h2 className={`text-3xl font-bold text-amarillo-dorado mb-6 flex items-center ${modoPantallaCompleta ? 'justify-start' : 'justify-center'}`}>
          <Ticket size={32} className="mr-3" />
          {modoPantallaCompleta ? 'VENTA RÁPIDA DE ENTRADAS' : 'Nueva Venta'}
        </h2>

        {modoPantallaCompleta && (
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <span className="text-sm text-gris-piedra">Modo de carga:</span>
            <button
              type="button"
              onClick={() => setModoPasoAPaso(false)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition border ${
                !modoPasoAPaso
                  ? 'bg-amarillo-dorado text-azul-oscuro border-amarillo-dorado shadow'
                  : 'bg-azul-oscuro/60 text-blanco-puro border-blanco-puro/20 hover:bg-azul-oscuro/80'
              }`}
            >
              Carga tradicional
            </button>
            <button
              type="button"
              onClick={() => setModoPasoAPaso(true)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition border ${
                modoPasoAPaso
                  ? 'bg-amarillo-dorado text-azul-oscuro border-amarillo-dorado shadow'
                  : 'bg-azul-oscuro/60 text-blanco-puro border-blanco-puro/20 hover:bg-azul-oscuro/80'
              }`}
            >
              Paso a paso
            </button>
          </div>
        )}

        {modoPantallaCompleta && modoPasoAPaso ? (
          <div className="space-y-6" data-venta-step>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                {pasoVenta > 1 && (
                  <button
                    type="button"
                    onClick={volverPaso}
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-azul-oscuro bg-opacity-70 text-amarillo-dorado border border-amarillo-dorado/40 hover:bg-opacity-90 transition"
                  >
                    <ArrowLeft size={18} />
                    Volver
                  </button>
                )}
                <span className="text-sm text-gris-piedra">
                  Paso {pasoVenta} de {pasosVenta.length}
                </span>
              </div>
              <div className="text-sm text-azul-claro">
                {pasosVenta.find((paso) => paso.numero === pasoVenta)?.titulo}
              </div>
            </div>

            <div className="w-full h-2 rounded-full bg-azul-oscuro bg-opacity-60 overflow-hidden">
              <div
                className="h-full bg-amarillo-dorado transition-all duration-300"
                style={{ width: `${(pasoVenta / pasosVenta.length) * 100}%` }}
              />
            </div>

            <div
              className={`step-card ${direccionPaso === 'forward' ? 'step-card-forward' : 'step-card-back'} ${
                pasoConError === pasoVenta ? 'step-card-error ring-2 ring-rojo-pasion/60' : ''
              } bg-azul-oscuro/70 border border-blanco-puro/20 rounded-2xl p-6 shadow-xl`}
            >
              {pasoVenta === 1 && (
                <div className="space-y-5">
                  <h3 className="text-xl font-semibold text-blanco-puro">Cantidad de personas</h3>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => actualizarCampoNumerico('cantidad', -1)}
                      className="px-3 py-2 rounded-lg bg-azul-oscuro bg-opacity-60 border border-blanco-puro/10 text-blanco-puro hover:bg-opacity-80 transition"
                      aria-label="Restar una entrada"
                    >
                      <Minus size={20} />
                    </button>
                    <input
                      ref={cantidadRef}
                      type="number"
                      value={nuevaVenta.cantidad}
                      onChange={e => setNuevaVenta(prev => ({ ...prev, cantidad: e.target.value }))}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          avanzarPaso();
                        }
                      }}
                      className="w-full px-4 py-3 bg-azul-oscuro bg-opacity-70 text-center text-3xl font-bold text-blanco-puro focus:outline-none focus:ring-2 focus:ring-amarillo-dorado/60 focus:border-amarillo-dorado rounded-lg border border-blanco-puro/20 transition"
                      onWheel={e => e.target.blur()}
                      min="0"
                    />
                    <button
                      type="button"
                      onClick={() => actualizarCampoNumerico('cantidad', 1)}
                      className="px-3 py-2 rounded-lg bg-azul-oscuro bg-opacity-60 border border-blanco-puro/10 text-blanco-puro hover:bg-opacity-80 transition"
                      aria-label="Sumar una entrada"
                    >
                      <Plus size={20} />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={avanzarPaso}
                    className="w-full px-6 py-3 bg-amarillo-dorado text-azul-oscuro rounded-xl font-bold text-lg shadow-lg hover:bg-[#D4AC0D] transition"
                  >
                    Continuar
                  </button>
                </div>
              )}

              {pasoVenta === 2 && (
                <div className="space-y-5">
                  <h3 className="text-xl font-semibold text-blanco-puro">Estadía</h3>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => actualizarCampoNumerico('diasEstadia', -1, 1)}
                      className="px-3 py-2 rounded-lg bg-azul-oscuro bg-opacity-60 border border-blanco-puro/10 text-blanco-puro hover:bg-opacity-80 transition"
                      aria-label="Restar un día"
                    >
                      <Minus size={20} />
                    </button>
                    <div className="relative flex-1">
                      <input
                        ref={diasRef}
                        type="number"
                        value={nuevaVenta.diasEstadia}
                        onChange={e => setNuevaVenta(prev => ({ ...prev, diasEstadia: e.target.value }))}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            avanzarPaso();
                          }
                        }}
                        className="w-full px-4 py-3 bg-azul-oscuro bg-opacity-70 text-center text-3xl font-bold text-blanco-puro focus:outline-none focus:ring-2 focus:ring-amarillo-dorado/60 focus:border-amarillo-dorado rounded-lg border border-blanco-puro/20 transition"
                        onWheel={e => e.target.blur()}
                        min="1"
                      />
                      <CalendarDays className="absolute right-4 top-1/2 -translate-y-1/2 text-amarillo-dorado drop-shadow" />
                    </div>
                    <button
                      type="button"
                      onClick={() => actualizarCampoNumerico('diasEstadia', 1, 1)}
                      className="px-3 py-2 rounded-lg bg-azul-oscuro bg-opacity-60 border border-blanco-puro/10 text-blanco-puro hover:bg-opacity-80 transition"
                      aria-label="Sumar un día"
                    >
                      <Plus size={20} />
                    </button>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gris-piedra">
                    <Clock className="text-amarillo-dorado" size={16} />
                    {fechaValidez
                      ? `Válido hasta ${format(fechaValidez, "EEEE dd 'de' MMMM 'a las' HH:mm", { locale: es })}`
                      : 'Seleccione días'}
                  </div>
                  <button
                    type="button"
                    onClick={avanzarPaso}
                    className="w-full px-6 py-3 bg-amarillo-dorado text-azul-oscuro rounded-xl font-bold text-lg shadow-lg hover:bg-[#D4AC0D] transition"
                  >
                    Continuar
                  </button>
                </div>
              )}

              {pasoVenta === 3 && (
                <div className="space-y-5">
                  <div>
                    <h3 className="text-xl font-semibold text-blanco-puro">Extras</h3>
                    <p className="text-sm text-amarillo-dorado/80">
                      Tocá cualquiera de las tarjetas para agregarlas a la venta.
                    </p>
                  </div>
                  {extrasActivos.length === 0 ? (
                    <div className="px-4 py-3 bg-azul-oscuro bg-opacity-60 rounded-lg text-sm text-gris-piedra border border-blanco-puro/10">
                      No hay extras configurados.
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-3">
                      {extrasActivos.map((extra) => {
                        const seleccionado = extrasSeleccionados.find((item) => item.id === extra.id);
                        return (
                          <div key={extra.id} className="w-full sm:w-auto">
                            <button
                              type="button"
                              onClick={() => manejarToggleExtra(extra)}
                              aria-pressed={seleccionado}
                              className={`w-full sm:w-auto px-4 py-3 rounded-xl border transition-all flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 text-left sm:text-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-amarillo-dorado/60 ${
                                seleccionado
                                  ? 'bg-amarillo-dorado text-azul-oscuro border-amarillo-dorado shadow-lg scale-[1.02]'
                                  : 'bg-azul-oscuro bg-opacity-60 text-blanco-puro border-amarillo-dorado/30 hover:border-amarillo-dorado/60 hover:bg-opacity-80 hover:-translate-y-0.5 shadow-md'
                              }`}
                              title={seleccionado ? 'Extra agregado. Volvé a tocar para quitarlo.' : 'Tocá para agregar este extra.'}
                            >
                              <span className="font-semibold">{extra.nombre}</span>
                              <span className="text-sm opacity-80">
                                ${Number(extra.valor || 0).toFixed(2)}
                              </span>
                            </button>
                            {seleccionado && (
                              <div className="mt-2 flex items-center justify-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => ajustarCantidadExtra(extra, -1)}
                                  className="p-2 rounded-lg bg-azul-oscuro bg-opacity-60 text-blanco-puro border border-blanco-puro/10 hover:bg-opacity-80 transition"
                                  aria-label={`Quitar ${extra.nombre}`}
                                >
                                  <Minus size={18} />
                                </button>
                                <input
                                  type="number"
                                  min="1"
                                  value={obtenerCantidadExtra(extra.id)}
                                  onChange={(e) => manejarCantidadExtra(extra.id, e.target.value)}
                                  className="w-20 px-3 py-2 bg-azul-oscuro bg-opacity-70 text-blanco-puro rounded-lg border border-blanco-puro/20 text-center focus:outline-none focus:ring-2 focus:ring-amarillo-dorado/60 focus:border-amarillo-dorado"
                                  onWheel={(e) => e.target.blur()}
                                />
                                <button
                                  type="button"
                                  onClick={() => ajustarCantidadExtra(extra, 1)}
                                  className="p-2 rounded-lg bg-azul-oscuro bg-opacity-60 text-blanco-puro border border-blanco-puro/10 hover:bg-opacity-80 transition"
                                  aria-label={`Agregar más de ${extra.nombre}`}
                                >
                                  <Plus size={18} />
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {subtotalExtras > 0 && (
                    <div className="text-sm text-amarillo-dorado text-right">
                      Subtotal de extras: ${subtotalExtras.toFixed(2)}
                    </div>
                  )}
                  <button
                    ref={continuarExtrasRef}
                    type="button"
                    onClick={avanzarPaso}
                    className="w-full px-6 py-3 bg-amarillo-dorado text-azul-oscuro rounded-xl font-bold text-lg shadow-lg hover:bg-[#D4AC0D] transition"
                  >
                    Continuar
                  </button>
                </div>
              )}

              {pasoVenta === 4 && (
                <div className="space-y-5">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-semibold text-blanco-puro">Método de pago</h3>
                    <button
                      onClick={() =>
                        setNuevaVenta(prev => ({
                          ...prev,
                          medio_pago_id_2: prev.medio_pago_id_2 != null ? null : 0,
                          monto_medio_pago_1: '',
                          monto_medio_pago_2: ''
                        }))
                      }
                      className={`text-sm px-3 py-1 rounded-md font-medium transition-all shadow ${
                        nuevaVenta.medio_pago_id_2 != null
                          ? 'bg-rojo-pasion text-blanco-puro hover:bg-red-700'
                          : 'bg-verde-esmeralda text-blanco-puro hover:bg-emerald-600'
                      }`}
                    >
                      {nuevaVenta.medio_pago_id_2 != null ? 'Usar solo un medio' : 'Agregar segundo medio'}
                    </button>
                  </div>

                  <div>
                    <h4 className="text-sm text-amarillo-dorado font-semibold mb-2">Medio principal</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {mediosDePago.map(medio => (
                        <button
                          key={medio.id}
                          onClick={() => {
                            setMedioPago(medio.id);
                            if (nuevaVenta.medio_pago_id_2 === medio.id) {
                              setNuevaVenta(prev => ({ ...prev, medio_pago_id_2: null }));
                            }
                          }}
                          className={`px-4 py-3 rounded-lg flex items-center justify-center border transition-all ${
                            medioPago === medio.id
                              ? 'bg-verde-esmeralda text-blanco-puro border-verde-esmeralda shadow-lg scale-105'
                              : 'bg-azul-oscuro bg-opacity-60 text-blanco-puro border-transparent hover:bg-opacity-70'
                          }`}
                        >
                          <CreditCard className="mr-2" size={18} />
                          {medio.nombre}
                        </button>
                      ))}
                    </div>
                  </div>

                  {nuevaVenta.medio_pago_id_2 != null && (
                    <div>
                      <h4 className="text-sm text-amarillo-dorado font-semibold mt-4 mb-2">Segundo medio</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {mediosDePago
                          .filter(m => m.id !== medioPago)
                          .map(medio => (
                            <button
                              key={medio.id}
                              onClick={() => setNuevaVenta(prev => ({ ...prev, medio_pago_id_2: medio.id }))}
                              className={`px-4 py-3 rounded-lg flex items-center justify-center border transition-all ${
                                nuevaVenta.medio_pago_id_2 === medio.id
                                  ? 'bg-azul-claro text-blanco-puro border-azul-claro shadow-lg scale-105'
                                  : 'bg-azul-oscuro bg-opacity-60 text-blanco-puro border-transparent hover:bg-opacity-70'
                              }`}
                            >
                              <CreditCard className="mr-2" size={18} />
                              {medio.nombre}
                            </button>
                          ))}
                      </div>
                    </div>
                  )}

                  {nuevaVenta.medio_pago_id_2 != null && (
                    <div className="mt-4 space-y-2">
                      <label className="text-sm text-blanco-puro font-medium">Distribución de montos</label>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          ref={monto1Ref}
                          type="number"
                          placeholder="Monto medio 1"
                          value={nuevaVenta.monto_medio_pago_1}
                          onChange={e => {
                            const total = totalCalculado;
                            const monto1 = Math.min(Number(e.target.value), total);
                            const monto2 = total - monto1;
                            setNuevaVenta(prev => ({
                              ...prev,
                              monto_medio_pago_1: monto1 > 0 ? monto1 : '',
                              monto_medio_pago_2: monto2 > 0 ? monto2 : ''
                            }));
                          }}
                          className="w-full px-4 py-2 bg-azul-oscuro bg-opacity-70 text-blanco-puro rounded-lg text-center border border-blanco-puro/20 focus:outline-none focus:ring-2 focus:ring-amarillo-dorado/60 focus:border-amarillo-dorado"
                          min="0"
                          step="0.01"
                        />
                        <input
                          ref={monto2Ref}
                          type="number"
                          placeholder="Monto medio 2"
                          value={nuevaVenta.monto_medio_pago_2}
                          onChange={e => {
                            const total = totalCalculado;
                            const monto2 = Math.min(Number(e.target.value), total);
                            const monto1 = total - monto2;
                            setNuevaVenta(prev => ({
                              ...prev,
                              monto_medio_pago_1: monto1 > 0 ? monto1 : '',
                              monto_medio_pago_2: monto2 > 0 ? monto2 : ''
                            }));
                          }}
                          className="w-full px-4 py-2 bg-azul-oscuro bg-opacity-70 text-blanco-puro rounded-lg text-center border border-blanco-puro/20 focus:outline-none focus:ring-2 focus:ring-amarillo-dorado/60 focus:border-amarillo-dorado"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <div className="text-sm text-gris-piedra text-center">
                        Total ingresado: ${(Number(nuevaVenta.monto_medio_pago_1 || 0) + Number(nuevaVenta.monto_medio_pago_2 || 0)).toFixed(2)}
                        {(Number(nuevaVenta.monto_medio_pago_1 || 0) + Number(nuevaVenta.monto_medio_pago_2 || 0)) !== totalCalculado && (
                          <span className="text-rojo-pasion ml-2 font-medium">⚠️ La suma no coincide con el total</span>
                        )}
                      </div>
                    </div>
                  )}

                  <button
                    ref={continuarPagoRef}
                    type="button"
                    onClick={avanzarPaso}
                    className="w-full px-6 py-3 bg-amarillo-dorado text-azul-oscuro rounded-xl font-bold text-lg shadow-lg hover:bg-[#D4AC0D] transition"
                  >
                    Continuar
                  </button>
                </div>
              )}

              {pasoVenta === 5 && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-blanco-puro">Confirmación</h3>
                    <p className="text-sm text-amarillo-dorado/80">
                      Revisá el resumen y continuá para validar con contraseña e imprimir.
                    </p>
                  </div>

                  <div className="space-y-3 text-sm text-blanco-puro">
                    <div className="flex justify-between">
                      <span className="text-gris-piedra">Personas</span>
                      <span className="font-semibold">{cantidadNumero}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gris-piedra">Estadía</span>
                      <span className="font-semibold">{diasNumero} día(s)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gris-piedra">Extras</span>
                      <span className="font-semibold">
                        {extrasSeleccionados.length > 0
                          ? extrasSeleccionados.map((extra) => `${extra.nombre} x${extra.cantidad}`).join(', ')
                          : 'Sin extras'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gris-piedra">Total</span>
                      <span className="text-amarillo-dorado font-semibold">${totalCalculado.toFixed(2)}</span>
                    </div>
                  </div>

                  <p className="text-xs text-azul-claro">
                    La venta se asignará al usuario que confirme con contraseña.
                  </p>

                  <button
                    ref={confirmarRef}
                    type="button"
                    onClick={avanzarPaso}
                    className="w-full px-6 py-4 bg-amarillo-dorado text-azul-oscuro rounded-xl font-bold text-xl shadow-lg hover:bg-[#D4AC0D] transition"
                  >
                    Finalizar y confirmar
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className={`grid ${gridClasses}`}>
              {/* Cantidad de Entradas */}
              <div className="space-y-2">
                <label className="block text-lg font-medium text-blanco-puro">Cantidad de Entradas</label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => actualizarCampoNumerico('cantidad', -1)}
                    className="px-3 py-2 rounded-lg bg-azul-oscuro bg-opacity-60 border border-blanco-puro/10 text-blanco-puro hover:bg-opacity-80 transition"
                    aria-label="Restar una entrada"
                  >
                    <Minus size={20} />
                  </button>
                  <input
                    ref={cantidadRef}
                    type="number"
                    value={nuevaVenta.cantidad}
                    onChange={e => setNuevaVenta(prev => ({ ...prev, cantidad: e.target.value }))}
                    className="w-full px-4 py-3 bg-azul-oscuro bg-opacity-70 text-center text-2xl font-bold text-blanco-puro focus:outline-none focus:ring-2 focus:ring-amarillo-dorado/60 focus:border-amarillo-dorado rounded-lg border border-blanco-puro/20 transition"
                    onWheel={e => e.target.blur()}
                    min="0"
                  />
                  <button
                    type="button"
                    onClick={() => actualizarCampoNumerico('cantidad', 1)}
                    className="px-3 py-2 rounded-lg bg-azul-oscuro bg-opacity-60 border border-blanco-puro/10 text-blanco-puro hover:bg-opacity-80 transition"
                    aria-label="Sumar una entrada"
                  >
                    <Plus size={20} />
                  </button>
                </div>
              </div>

              {/* Días de Estadía */}
              <div className="space-y-2">
                <label className="block text-lg font-medium text-blanco-puro">Días de estadía</label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => actualizarCampoNumerico('diasEstadia', -1, 1)}
                    className="px-3 py-2 rounded-lg bg-azul-oscuro bg-opacity-60 border border-blanco-puro/10 text-blanco-puro hover:bg-opacity-80 transition"
                    aria-label="Restar un día"
                  >
                    <Minus size={20} />
                  </button>
                  <div className="relative flex-1">
                    <input
                      type="number"
                      value={nuevaVenta.diasEstadia}
                      onChange={e => setNuevaVenta(prev => ({ ...prev, diasEstadia: e.target.value }))}
                      className="w-full px-4 py-3 bg-azul-oscuro bg-opacity-70 text-center text-2xl font-bold text-blanco-puro focus:outline-none focus:ring-2 focus:ring-amarillo-dorado/60 focus:border-amarillo-dorado rounded-lg border border-blanco-puro/20 transition"
                      onWheel={e => e.target.blur()}
                      min="1"
                    />
                    <CalendarDays className="absolute right-4 top-1/2 -translate-y-1/2 text-amarillo-dorado drop-shadow" />
                  </div>
                  <button
                    type="button"
                    onClick={() => actualizarCampoNumerico('diasEstadia', 1, 1)}
                    className="px-3 py-2 rounded-lg bg-azul-oscuro bg-opacity-60 border border-blanco-puro/10 text-blanco-puro hover:bg-opacity-80 transition"
                    aria-label="Sumar un día"
                  >
                    <Plus size={20} />
                  </button>
                </div>
              </div>

              {/* Extras */}
              <div className={`space-y-3 ${modoPantallaCompleta ? 'lg:col-span-2' : ''}`}>
                <label className="block text-lg font-medium text-blanco-puro">Extras</label>
                <p className="text-sm text-amarillo-dorado/80">
                  Tocá cualquiera de las tarjetas para agregarlas a la venta. Podés ajustar la cantidad luego.
                </p>
                {extrasActivos.length === 0 ? (
                  <div className="px-4 py-3 bg-azul-oscuro bg-opacity-60 rounded-lg text-sm text-gris-piedra border border-blanco-puro/10">
                    No hay extras configurados.
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-3">
                    {extrasActivos.map((extra) => {
                      const seleccionado = extrasSeleccionados.find((item) => item.id === extra.id);
                      return (
                        <div key={extra.id} className="w-full sm:w-auto">
                          <button
                            type="button"
                            onClick={() => manejarToggleExtra(extra)}
                            aria-pressed={seleccionado}
                            className={`w-full sm:w-auto px-4 py-3 rounded-xl border transition-all flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 text-left sm:text-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-amarillo-dorado/60 ${
                              seleccionado
                                ? 'bg-amarillo-dorado text-azul-oscuro border-amarillo-dorado shadow-lg scale-[1.02]'
                                : 'bg-azul-oscuro bg-opacity-60 text-blanco-puro border-amarillo-dorado/30 hover:border-amarillo-dorado/60 hover:bg-opacity-80 hover:-translate-y-0.5 shadow-md'
                            }`}
                            title={seleccionado ? 'Extra agregado. Volvé a tocar para quitarlo.' : 'Tocá para agregar este extra.'}
                          >
                            <span className="font-semibold">{extra.nombre}</span>
                            <span className="text-sm opacity-80">
                              ${Number(extra.valor || 0).toFixed(2)}
                            </span>
                          </button>
                          {seleccionado && (
                            <div className="mt-2 flex items-center justify-center gap-2">
                              <button
                                type="button"
                                onClick={() => ajustarCantidadExtra(extra, -1)}
                                className="p-2 rounded-lg bg-azul-oscuro bg-opacity-60 text-blanco-puro border border-blanco-puro/10 hover:bg-opacity-80 transition"
                                aria-label={`Quitar ${extra.nombre}`}
                              >
                                <Minus size={18} />
                              </button>
                              <input
                                type="number"
                                min="1"
                                value={obtenerCantidadExtra(extra.id)}
                                onChange={(e) => manejarCantidadExtra(extra.id, e.target.value)}
                                className="w-20 px-3 py-2 bg-azul-oscuro bg-opacity-70 text-blanco-puro rounded-lg border border-blanco-puro/20 text-center focus:outline-none focus:ring-2 focus:ring-amarillo-dorado/60 focus:border-amarillo-dorado"
                                onWheel={(e) => e.target.blur()}
                              />
                              <button
                                type="button"
                                onClick={() => ajustarCantidadExtra(extra, 1)}
                                className="p-2 rounded-lg bg-azul-oscuro bg-opacity-60 text-blanco-puro border border-blanco-puro/10 hover:bg-opacity-80 transition"
                                aria-label={`Agregar más de ${extra.nombre}`}
                              >
                                <Plus size={18} />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {subtotalExtras > 0 && (
                  <div className="text-sm text-amarillo-dorado text-right">
                    Subtotal de extras: ${subtotalExtras.toFixed(2)}
                  </div>
                )}
              </div>

              {/* Valor Total */}
              <div className="space-y-2">
                <label className="block text-lg font-medium text-blanco-puro">Valor Total</label>
                <div className="px-4 py-3 bg-azul-oscuro bg-opacity-70 rounded-lg text-center border border-blanco-puro/20">
                  <div className="flex justify-between text-sm text-gris-piedra tracking-wide">
                    <span>Base</span>
                    <span>${totalBase.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gris-piedra tracking-wide">
                    <span>Extras</span>
                    <span>+${subtotalExtras.toFixed(2)}</span>
                  </div>
                  <div className="mt-3 text-3xl font-bold text-amarillo-dorado drop-shadow">
                    ${totalCalculado.toFixed(2)}
                  </div>
                </div>
              </div>

              {/* Válido hasta */}
              <div className="space-y-2">
                <label className="block text-lg font-medium text-blanco-puro">Válido hasta</label>
                <div className="flex items-center justify-between px-4 py-3 bg-azul-oscuro bg-opacity-70 rounded-lg text-xl text-blanco-puro border border-blanco-puro/20">
                  <div className="flex items-center space-x-2">
                    <Clock className="text-amarillo-dorado" />
                    <span>
                      {fechaValidez
                        ? format(fechaValidez, "EEEE dd 'de' MMMM 'a las' HH:mm", { locale: es })
                        : 'Seleccione días'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Usuario + Botones */}
              <div className="space-y-2">
                <label className="block text-lg font-medium text-blanco-puro">Usuario</label>
                <div className="w-full px-4 py-3 bg-azul-oscuro bg-opacity-70 text-azul-claro rounded-lg border border-blanco-puro/20">
                  La venta se asignará automáticamente al operador que ingrese su contraseña al confirmar.
                </div>
              </div>

              {/* Medios de Pago */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-blanco-puro">Seleccionar Medio(s) de Pago</h3>
                  <button
                    onClick={() =>
                      setNuevaVenta(prev => ({
                        ...prev,
                        medio_pago_id_2: prev.medio_pago_id_2 != null ? null : 0,
                        monto_medio_pago_1: '',
                        monto_medio_pago_2: ''
                      }))
                    }
                    className={`text-sm px-3 py-1 rounded-md font-medium transition-all shadow ${
                      nuevaVenta.medio_pago_id_2 != null
                        ? 'bg-rojo-pasion text-blanco-puro hover:bg-red-700'
                        : 'bg-verde-esmeralda text-blanco-puro hover:bg-emerald-600'
                    }`}
                  >
                    {nuevaVenta.medio_pago_id_2 != null ? 'Usar solo un medio' : 'Agregar segundo medio'}
                  </button>
                </div>

                {/* Primer Medio */}
                <div>
                  <h4 className="text-sm text-amarillo-dorado font-semibold mb-1">Medio de Pago</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {mediosDePago.map(medio => (
                      <button
                        key={medio.id}
                        onClick={() => {
                          setMedioPago(medio.id);
                          if (nuevaVenta.medio_pago_id_2 === medio.id) {
                            setNuevaVenta(prev => ({ ...prev, medio_pago_id_2: null }));
                          }
                        }}
                        className={`px-4 py-3 rounded-lg flex items-center justify-center border transition-all ${
                          medioPago === medio.id
                            ? 'bg-verde-esmeralda text-blanco-puro border-verde-esmeralda shadow-lg scale-105'
                            : 'bg-azul-oscuro bg-opacity-60 text-blanco-puro border-transparent hover:bg-opacity-70'
                        }`}
                      >
                        <CreditCard className="mr-2" size={18} />
                        {medio.nombre}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Segundo Medio */}
                {nuevaVenta.medio_pago_id_2 != null && (
                  <div>
                    <h4 className="text-sm text-amarillo-dorado font-semibold mt-4 mb-1">Segundo Medio de Pago</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {mediosDePago
                        .filter(m => m.id !== medioPago)
                        .map(medio => (
                          <button
                            key={medio.id}
                            onClick={() => setNuevaVenta(prev => ({ ...prev, medio_pago_id_2: medio.id }))}
                            className={`px-4 py-3 rounded-lg flex items-center justify-center border transition-all ${
                              nuevaVenta.medio_pago_id_2 === medio.id
                                ? 'bg-azul-claro text-blanco-puro border-azul-claro shadow-lg scale-105'
                                : 'bg-azul-oscuro bg-opacity-60 text-blanco-puro border-transparent hover:bg-opacity-70'
                            }`}
                          >
                            <CreditCard className="mr-2" size={18} />
                            {medio.nombre}
                          </button>
                        ))}
                    </div>
                  </div>
                )}

                {/* Distribución de Montos */}
                {nuevaVenta.medio_pago_id_2 != null && (
                  <div className="mt-4 space-y-2">
                    <label className="text-sm text-blanco-puro font-medium">Distribución de montos</label>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        ref={monto1Ref}
                        type="number"
                        placeholder="Monto medio 1"
                        value={nuevaVenta.monto_medio_pago_1}
                        onChange={e => {
                          const total = totalCalculado;
                          const monto1 = Math.min(Number(e.target.value), total);
                          const monto2 = total - monto1;
                          setNuevaVenta(prev => ({
                            ...prev,
                            monto_medio_pago_1: monto1 > 0 ? monto1 : '',
                            monto_medio_pago_2: monto2 > 0 ? monto2 : ''
                          }));
                        }}
                        className="w-full px-4 py-2 bg-azul-oscuro bg-opacity-70 text-blanco-puro rounded-lg text-center border border-blanco-puro/20 focus:outline-none focus:ring-2 focus:ring-amarillo-dorado/60 focus:border-amarillo-dorado"
                        min="0"
                        step="0.01"
                      />
                      <input
                        ref={monto2Ref}
                        type="number"
                        placeholder="Monto medio 2"
                        value={nuevaVenta.monto_medio_pago_2}
                        onChange={e => {
                          const total = totalCalculado;
                          const monto2 = Math.min(Number(e.target.value), total);
                          const monto1 = total - monto2;
                          setNuevaVenta(prev => ({
                            ...prev,
                            monto_medio_pago_1: monto1 > 0 ? monto1 : '',
                            monto_medio_pago_2: monto2 > 0 ? monto2 : ''
                          }));
                        }}
                        className="w-full px-4 py-2 bg-azul-oscuro bg-opacity-70 text-blanco-puro rounded-lg text-center border border-blanco-puro/20 focus:outline-none focus:ring-2 focus:ring-amarillo-dorado/60 focus:border-amarillo-dorado"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div className="text-sm text-gris-piedra text-center">
                      Total ingresado: ${(Number(nuevaVenta.monto_medio_pago_1 || 0) + Number(nuevaVenta.monto_medio_pago_2 || 0)).toFixed(2)}
                      {(Number(nuevaVenta.monto_medio_pago_1 || 0) + Number(nuevaVenta.monto_medio_pago_2 || 0)) !== totalCalculado && (
                        <span className="text-rojo-pasion ml-2 font-medium">⚠️ La suma no coincide con el total</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Botones abajo */}
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <button
                onClick={limpiarFormulario}
                className="px-6 py-3 bg-rojo-pasion bg-opacity-80 hover:bg-opacity-100 text-blanco-puro rounded-lg font-medium flex items-center border border-gris-piedra border-opacity-30"
              >
                <RefreshCw className="mr-2" size={18} />
                Limpiar
              </button>

              <button
                onClick={handleAgregarVenta}
                className="px-8 py-3 bg-verde-esmeralda hover:bg-emerald-600 text-blanco-puro rounded-lg font-bold text-lg flex items-center border border-gris-piedra border-opacity-30"
              >
                <Plus className="mr-2" size={20} />
                Registrar Venta
              </button>

              {!modoPantallaCompleta && (
                <button
                  onClick={() => setModoPantallaCompleta(true)}
                  className="px-6 py-3 bg-azul-oscuro bg-opacity-70 hover:bg-opacity-90 text-amarillo-dorado rounded-lg font-semibold flex items-center border border-gris-piedra border-opacity-30"
                  title="Activar pantalla completa"
                >
                  <ArrowRight className="mr-2" />
                  Pantalla completa (F10)
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FormularioVenta;
